import React, { useState, useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Container from "@/components/marketing/Container";
import ReactMarkdown from "react-markdown";
import { HELP_SECTIONS } from "@/lib/helpContent";

/**
 * Two-column documentation layout: sticky category sidebar (left) +
 * accordion sections (right). Search filtering hides non-matching
 * questions; categories with no visible questions are hidden from the
 * right column and their sidebar entries are dimmed. Active section is
 * highlighted via IntersectionObserver while scrolling.
 */
export default function HelpFaqList({ search }) {
  const [activeId, setActiveId] = useState(HELP_SECTIONS[0]?.id || "");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const term = search.trim().toLowerCase();

  const filtered = !term
    ? HELP_SECTIONS
    : HELP_SECTIONS.map((s) => ({
        ...s,
        faqs: s.faqs.filter(
          (f) => f.q.toLowerCase().includes(term) || f.a.toLowerCase().includes(term)
        ),
      })).filter((s) => s.faqs.length > 0);

  const filteredIds = new Set(filtered.map((s) => s.id));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -75% 0px", threshold: 0 }
    );
    filtered.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const onSidebarClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
    setSidebarOpen(false);
  };

  if (filtered.length === 0) {
    return (
      <section className="bg-[#f7f7f9] pb-16 lg:pb-24">
        <Container className="max-w-2xl text-center py-16">
          <p className="text-steel mb-4">No results for &ldquo;{search}&rdquo;.</p>
          <p className="text-sm text-steel/70">
            Try a different search, or{" "}
            <Link to="/contact" className="text-cobalt font-medium hover:underline">contact our team</Link>.
          </p>
        </Container>
      </section>
    );
  }

  return (
    <section className="bg-[#f7f7f9] pb-16 lg:pb-24">
      <Container>
        <div className="grid lg:grid-cols-[220px_1fr] gap-8 lg:gap-12">
          {/* LEFT: sticky category sidebar */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <button
              className="lg:hidden w-full flex items-center justify-between py-3 px-4 border border-[#e8e6ef] rounded-lg text-bone text-sm font-medium bg-white"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-expanded={sidebarOpen}
            >
              Categories
              <ChevronDown
                className={`w-4 h-4 transition-transform ${sidebarOpen ? "rotate-180" : ""}`}
              />
            </button>

            <nav className={`${sidebarOpen ? "block mt-4" : "hidden"} lg:block`}>
              <p className="hidden lg:block font-mono text-xs uppercase tracking-widest text-steel mb-4">
                Categories
              </p>
              <ul className="space-y-2.5 lg:max-h-[calc(100vh-14rem)] lg:overflow-y-auto lg:pr-2">
                {HELP_SECTIONS.map((s) => {
                  const isActive = activeId === s.id;
                  const isDimmed = !filteredIds.has(s.id);
                  return (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => onSidebarClick(e, s.id)}
                        className={`block text-sm leading-snug transition-colors ${
                          isDimmed ? "opacity-40 pointer-events-none cursor-default" : ""
                        } ${
                          isActive
                            ? "text-cobalt font-medium"
                            : "text-prose hover:text-bone"
                        }`}
                      >
                        {s.title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* RIGHT: category sections */}
          <div className="max-w-[760px] space-y-16 lg:space-y-24">
            {filtered.map((section) => (
              <div key={section.id} id={section.id} className="scroll-mt-32">
                {/* Section header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-cobalt/10 shrink-0">
                    <section.icon className="w-[18px] h-[18px] text-cobalt" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-bone">{section.title}</h2>
                </div>
                <div className="h-px bg-[#ececf2] mb-4" />

                {/* Accordions */}
                <Accordion type="single" collapsible>
                  {section.faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`${section.id}-${i}`} className="border-border">
                      <AccordionTrigger className="text-bone hover:text-cobalt text-left font-heading text-base py-5">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-steel text-sm leading-relaxed pb-5 [&_p]:mb-0 [&_a]:text-cobalt [&_a]:font-medium [&_a]:hover:underline">
                        <ReactMarkdown
                          components={{
                            a: ({ node, href, children, ...rest }) => {
                              if (href && href.startsWith("/")) {
                                return <Link to={href} {...rest}>{children}</Link>;
                              }
                              return <a href={href} {...rest}>{children}</a>;
                            }
                          }}
                        >
                          {faq.a}
                        </ReactMarkdown>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}